import { takeLatest, select, put } from 'redux-saga/effects'
import * as actions from '../../actions'
import { configurationLineItemSaga } from './configuration'
import {
  internetLineItemTypes,
  internetConfigurationItems,
  staticIpJustificationCounts,
  defaultIPSelection,
  configTypes
} from '../../constants/configuration'
import { 
  selectLineItemByType,
  selectExistingServiceItemById 
} from '../../selectors/configuration'
import { selectIsAddendumSelected } from '../../selectors/session'
import { getConfigItem, getSelectedConfigItem } from '../../utils/configuration'

const lineItemSagas = {
  [internetLineItemTypes.staticIps]: updateStaticIPs,
  [internetLineItemTypes.internetEquipment]: setInternetEquipmentSaga,
  [internetLineItemTypes.connectionPro]: updateConnectionProSaga,
  [internetLineItemTypes.wirelessConnect]: updateWirelessConnectSaga,
  [internetLineItemTypes.securityEdge]: updateSecurityEdgeSaga,
  [internetLineItemTypes.wifiAP]: updateWifiExtendersSaga,
  [internetLineItemTypes.connectionProExtBat]: updateConnectionProExtBatSaga
}

export function* updateWifiExtendersSaga({ payload }) {
  const { item, lineItem } = payload
  // Select the wifi Extenders line item
  const wifiExtendersLineItem = yield select(
    selectLineItemByType(internetLineItemTypes.wifiExtenders)
  )
  const wifiExtendersItem = getConfigItem(wifiExtendersLineItem)
  const isAddendumSelected = yield select(selectIsAddendumSelected)
  function* resetWifiExtenders() {
    yield put(
      actions.disableConfig(internetLineItemTypes.wifiExtenders, true)
    )
    yield put(
      actions.updateConfigItem({
        lineItem: wifiExtendersLineItem,
        item: { ...wifiExtendersItem, isSelected: false, quantity: 0 }
      })
    )
  }

  if (wifiExtendersLineItem) {
    if (item.id === internetConfigurationItems.wifiStandard) {
      // If it's wifi Standard, update the wifi Extenders line item to be enabled
      yield put(
        actions.disableConfig(internetLineItemTypes.wifiExtenders, false)
      )
    }
    else if (isAddendumSelected 
      && item.id === internetConfigurationItems.noSelection) {
      // If addendum flow and no selection is selected, check if existing customer has WiFi Standard
      const existingWifiStandard = yield select(
        selectExistingServiceItemById(configTypes.internet, 
          internetConfigurationItems.wifiStandard)
      )
      
      if (existingWifiStandard) {
        // Keep WiFi Extenders enabled for existing customers with WiFi Standard
        yield put(
          actions.disableConfig(internetLineItemTypes.wifiExtenders, false)
        )
      } else {
        // Disable if no existing WiFi Standard
        yield* resetWifiExtenders()
      }
    }
    else {
      // If it's wifiPro or wifiPro2 or No wifi Selected, disable and reset the wifi Extenders line item
      yield* resetWifiExtenders()
    }
  }
  // Finally, update the config item with the provided lineItem and item
  yield put(
    actions.updateConfigItem({
      lineItem,
      item
    })
  )
}

export function* setInternetEquipmentSaga({ payload }) {
  const { item, lineItem } = payload
  const connectionProLineItem = yield select(
    selectLineItemByType(internetLineItemTypes.connectionPro)
  )
  const connectionProItem = getConfigItem(connectionProLineItem)
  const selectedTierSpeedLineItem = yield select(
    selectLineItemByType(internetLineItemTypes.selectedTierSpeed)
  )
  let selectedTierSpeedItem = getConfigItem(selectedTierSpeedLineItem)
  const state = yield select()
  const { uploadDownloadSpeed, downgradedGenesisSpeed = '' } = state.configuration.offer
  if (item.id === internetConfigurationItems.customerOwnedEquipment) {
    if (item.id === internetConfigurationItems.customerOwnedEquipment
      && downgradedGenesisSpeed) {
      selectedTierSpeedItem = {
        ...selectedTierSpeedItem,
        value: downgradedGenesisSpeed
      }
      yield put(
        actions.updateConfigItem({
          lineItem: selectedTierSpeedLineItem,
          item: selectedTierSpeedItem
        })
      )
    }
    // if there is no error and they chose customer own equipment, toggle the off the internet options
    yield put(actions.toggleInternetOptions(false))

    yield put(actions.updateConfigItem({ lineItem, item }))
    if (connectionProLineItem) {
      if (connectionProLineItem.hasAncillaryPrices) {
        const noSelectionItem =
          connectionProLineItem.configurationItems[
            internetConfigurationItems.noConnectionPro
          ]

        yield put(
          actions.configureItem(configTypes.internet, {
            lineItem: connectionProLineItem,
            item: { ...noSelectionItem, isSelected: true, quantity: 1 }
          })
        )
      } else {
        yield put(
          actions.configureItem(configTypes.internet, {
            lineItem: connectionProLineItem,
            item: { ...connectionProItem, isSelected: false, quantity: 0 }
          })
        )
      }
    }
  } else {
    const previousConfigItem = getSelectedConfigItem(lineItem)
    yield put(actions.updateConfigItem({ lineItem, item }))
    // toggle the internet options back on if pervious selection was "not customer owned equipment"
    if (
      previousConfigItem?.id ===
      internetConfigurationItems.customerOwnedEquipment
    ) {
      if(selectedTierSpeedItem) {
        selectedTierSpeedItem = {
          ...selectedTierSpeedItem,
          value: uploadDownloadSpeed
        }
        yield put(
          actions.updateConfigItem({
            lineItem: selectedTierSpeedLineItem,
            item: selectedTierSpeedItem
          })
        )
      }
      yield put(actions.toggleInternetOptions(true))
    }
  }
}

export function* toggleInternetOptions({ payload }) {
  const { enabled } = payload
  const connectionProLineItem = yield select(
    selectLineItemByType(internetLineItemTypes.connectionPro)
  )
  const connectionProItem = getConfigItem(connectionProLineItem)
  const wifiBasicLineItem = yield select(
    selectLineItemByType(internetLineItemTypes.wifi)
  )
  const wirelessConnectLineItem = yield select(
    selectLineItemByType(internetLineItemTypes.wirelessConnect))

  const wirelessConnectItem = getConfigItem(wirelessConnectLineItem)

  const wirelessConnectExtBatLineItem = yield select(
    selectLineItemByType(internetLineItemTypes.wirelessConnectExtBat)
  )
  const wirelessConnectExtBatItem = getConfigItem(wirelessConnectExtBatLineItem)
  
  const wifiAPLineItem = yield select(
    selectLineItemByType(internetLineItemTypes.wifiAP)
  )
  const staticIpLineItem = yield select(
    selectLineItemByType(internetLineItemTypes.staticIps)
  )

  const securityEdgeLineItem = yield select(
    selectLineItemByType(internetLineItemTypes.securityEdge)
  )
  const securityEdgeItem = getConfigItem(securityEdgeLineItem)

  const wifiExtendersLineItem = yield select(
    selectLineItemByType(internetLineItemTypes.wifiExtenders)
  )
  const wifiExtendersItem = getConfigItem(wifiExtendersLineItem)

  // If it's customer owned equipment disable and reset the wifi Extenders line item to 0
  if (!!wifiExtendersLineItem && !!wifiExtendersItem) {
    yield put(
      actions.updateConfigItem({
        lineItem: wifiExtendersLineItem,
        item: { ...wifiExtendersItem, isSelected: false, quantity: 0 }
      })
    )
    yield put(
      actions.disableConfig(internetLineItemTypes.wifiExtenders, true)
    )
  }
  // When toggling, we always want the line items to be set to no selection
  // Then determine if it is enabled/disabled from the payload
  if (connectionProLineItem) {
    if (connectionProItem.id !== internetConfigurationItems.noConnectionPro) {
      yield put(
        actions.updateConfigItem({
          lineItem: connectionProLineItem,
          item: {
            ...connectionProItem,
            isSelected: false,
            quantity: 0
          }
        })
      )
    }
    yield put(
      actions.disableConfig(internetLineItemTypes.connectionPro, !enabled)
    )
  }

  if (wirelessConnectLineItem) {
    yield put(
      actions.disableConfig(internetLineItemTypes.wirelessConnect, !enabled)
    )
    if( wirelessConnectItem.hasAncillaryPrices || wirelessConnectItem.id 
        === internetConfigurationItems.noWirelessConnect) {
      const noSelectionItem =
          wirelessConnectLineItem.configurationItems[
            internetConfigurationItems.noWirelessConnect
          ]
      yield put(
        actions.configureItem(configTypes.internet, {
          lineItem: wirelessConnectLineItem,
          item: { ...noSelectionItem, isSelected: true, quantity: 1 }
        })
      )
    } else {
      yield put(
        actions.updateConfigItem({
          lineItem: wirelessConnectLineItem,
          item: {
            ...wirelessConnectItem,
            isSelected: false,
            quantity: 0
          }
        })
      )
    }
  }
  if (wirelessConnectExtBatLineItem) {
    yield put(
      actions.updateConfigItem({
        lineItem: wirelessConnectExtBatLineItem,
        item: {
          ...wirelessConnectExtBatItem,
          isSelected: false,
          quantity: 0
        }
      })
    )
  }

  if (securityEdgeLineItem) {
    const noSecurityEdgeItem =
      securityEdgeLineItem?.configurationItems[
        internetConfigurationItems.noSecurityEdge
      ]

    const securityEdgeAddonsCovLineItem = yield select(
      selectLineItemByType(internetLineItemTypes.securityEdgeAddons)
    )
    const securityEdgeAddonsCovItem = 
      getConfigItem(securityEdgeAddonsCovLineItem)

    // has ancillary prices and with no security edge selected in config items
    if (
      securityEdgeItem.id !== internetConfigurationItems.noSecurityEdge &&
      !noSecurityEdgeItem
    ) {
      yield put(
        actions.updateConfigItem({
          lineItem: securityEdgeLineItem,
          item: {
            ...securityEdgeItem,
            isSelected: false,
            quantity: 0
          }
        })
      )
    }

    // has ancillary prices and with no security edge selected in config items
    if (securityEdgeLineItem?.hasAncillaryPrices && noSecurityEdgeItem) {
      const noSelectionItem =
        securityEdgeLineItem.configurationItems[
          internetConfigurationItems.noSecurityEdge
        ]

      yield put(
        actions.updateConfigItem({
          lineItem: securityEdgeLineItem,
          item: { ...noSelectionItem, isSelected: true, quantity: 1 }
        })
      )
      if (securityEdgeAddonsCovLineItem) {
        yield put(
          actions.updateConfigItem({
            lineItem: securityEdgeAddonsCovLineItem,
            item: {
              ...securityEdgeAddonsCovItem,
              isSelected: false,
              quantity: 0
            }
          })
        )
      }
    } else {
      // has no ancillary prices and SE checkbox
      if (securityEdgeAddonsCovItem?.isSelected) {
        yield put(
          actions.updateConfigItem({
            lineItem: securityEdgeAddonsCovLineItem,
            item: {
              ...securityEdgeAddonsCovItem,
              isSelected: false,
              quantity: 0
            }
          })
        )
      }
      yield put(
        actions.updateConfigItem({
          lineItem: securityEdgeLineItem,
          item: { ...securityEdgeItem, isSelected: false, quantity: 0 }
        })
      )
    }
    yield put(
      actions.disableConfig(internetLineItemTypes.securityEdge, !enabled)
    )
  }

  if (wifiAPLineItem?.configurationItems[internetConfigurationItems.noWifi]) {
    yield put(
      actions.updateConfigItem({
        lineItem: wifiAPLineItem,
        item: {
          ...wifiAPLineItem.configurationItems[
            internetConfigurationItems.noWifi
          ],
          isSelected: true,
          quantity: 1
        }
      })
    )
    yield put(actions.disableConfig(wifiAPLineItem.type, !enabled))
  }

  if (
    wifiBasicLineItem?.configurationItems[internetConfigurationItems.noWifi]
  ) {
    yield put(
      actions.updateConfigItem({
        lineItem: wifiBasicLineItem,
        item: {
          ...wifiBasicLineItem.configurationItems[
            internetConfigurationItems.noWifi
          ],
          isSelected: true,
          quantity: 1
        }
      })
    )
    yield put(actions.disableConfig(wifiBasicLineItem.type, !enabled))
  }

  yield put(
    actions.updateConfigItem({
      lineItem: staticIpLineItem,
      item: {
        ...staticIpLineItem.configurationItems[
          internetConfigurationItems.noStaticIp
        ],
        isSelected: true,
        quantity: 1
      }
    })
  )
  yield put(actions.disableConfig(internetLineItemTypes.staticIps, !enabled))
}

export function* updateStaticIPs({ payload }) {
  const { lineItem, item } = payload
  const { staticIpJustifications } = item.attributes
  const currentIpSelections = getSelectedConfigItem(lineItem)
  const ipCount = staticIpJustificationCounts[item.id]
  const currentIPs = currentIpSelections.attributes.staticIpJustifications || []
  let updatedJustifications = []

  // Create new array with copied values (ip count change)
  if (currentIPs.length !== ipCount) {
    updatedJustifications = new Array(ipCount > 1 ? ipCount : 0)
      .fill(defaultIPSelection)
      .map(
        (val, index) => currentIPs[index] || val // Map current value if set
      )
  } else {
    // Set new values (ip justification change)
    updatedJustifications = staticIpJustifications
  }

  yield put(
    actions.updateConfigItem({
      lineItem,
      item: {
        ...item,
        attributes: {
          ...item.attributes,
          staticIpJustifications: updatedJustifications
        }
      }
    })
  )
}

export function* updateConnectionProSaga({ payload }) {
  const { lineItem, item } = payload

  yield put(
    actions.updateConfigItem({
      lineItem,
      item
    })
  )
  const connectionProExtBatLineItem = yield select(
    selectLineItemByType(internetLineItemTypes.connectionProExtBat)
  )
  const connectionProExtBatItem = getConfigItem(connectionProExtBatLineItem)

  // unset battery if no selection or unchecked box
  if (
    (item.id === internetConfigurationItems.noConnectionPro ||
      !item.isSelected) &&
    connectionProExtBatLineItem
  ) {
    yield put(
      actions.updateConfigItem({
        lineItem: connectionProExtBatLineItem,
        item: {
          ...connectionProExtBatItem,
          isSelected: false,
          quantity: 0
        }
      })
    )
  }
}

export function* updateWirelessConnectSaga({ payload }) {
  const { lineItem, item } = payload
  const isAddendumSelected = yield select(selectIsAddendumSelected)

  yield put(
    actions.updateConfigItem({
      lineItem,
      item
    })
  )
  const wirelessConnectExtBat = yield select(
    selectLineItemByType(internetLineItemTypes.wirelessConnectExtBat)
  )
  const wirelessConnectExtBatItem = getConfigItem(wirelessConnectExtBat)

  // unset battery if no selection or unchecked box
  if (
    (item.id === internetConfigurationItems.noWirelessConnect ||
      !item.isSelected) && wirelessConnectExtBat) {
    yield put(
      actions.updateConfigItem({
        lineItem: wirelessConnectExtBat,
        item: {
          ...wirelessConnectExtBatItem,
          isSelected: false,
          quantity: 0
        }
      })
    )
  }

  // In addendum, if Wireless Connect is selected, disable Connection Pro and its Extended Battery
  // If unchecked or no selection, re-enable Connection Pro and its Extended Battery
  if (isAddendumSelected) {
    const isWirelessConnectSelected = item.isSelected && 
      item.id !== internetConfigurationItems.noWirelessConnect

    const connectionProExtBatLineItem = yield select(
      selectLineItemByType(internetLineItemTypes.connectionProExtBat)
    )
    const connectionProExtBatItem = getConfigItem(connectionProExtBatLineItem)
    
    if (connectionProExtBatLineItem) {
      if (isWirelessConnectSelected) {
        // Disable and unset Connection Pro Extended Battery
        yield put(
          actions.updateConfigItem({
            lineItem: connectionProExtBatLineItem,
            item: {
              ...connectionProExtBatItem,
              isSelected: false,
              quantity: 0
            }
          })
        )
      }
      yield put(
        actions.disableConfig(internetLineItemTypes.connectionProExtBat, 
          isWirelessConnectSelected)
      )
    }
  }
}

export function* updateSecurityEdgeSaga({ payload }) {
  const { lineItem, item } = payload

  yield put(
    actions.updateConfigItem({
      lineItem,
      item
    })
  )
  const securityEdgeAddonsCovLineItem = yield select(
    selectLineItemByType(internetLineItemTypes.securityEdgeAddons)
  )
  const securityEdgeAddonsCovItem = getConfigItem(securityEdgeAddonsCovLineItem)

  // unset battery if no selection or unchecked box
  if (
    (item.id === internetConfigurationItems.noSecurityEdge ||
      !item.isSelected) &&
    securityEdgeAddonsCovLineItem
  ) {
    yield put(
      actions.updateConfigItem({
        lineItem: securityEdgeAddonsCovLineItem,
        item: {
          ...securityEdgeAddonsCovLineItem.configurationItems[
            internetConfigurationItems.noSecurityEdgeCov
          ],
          isSelected: true,
          quantity: 1
        }
      })
    )
  }
}

export function* updateConnectionProExtBatSaga({ payload }) {
  const { lineItem, item } = payload
  const isAddendumSelected = yield select(selectIsAddendumSelected)

  yield put(
    actions.updateConfigItem({
      lineItem,
      item
    })
  )

  // In addendum, if Connection Pro Extended Battery is selected, disable Wireless Connect and its Extended Battery
  // If unchecked, re-enable Wireless Connect and its Extended Battery
  if (isAddendumSelected) {
    const wirelessConnectLineItem = yield select(
      selectLineItemByType(internetLineItemTypes.wirelessConnect)
    )
    
    const wirelessConnectExtBatLineItem = yield select(
      selectLineItemByType(internetLineItemTypes.wirelessConnectExtBat)
    )
    
    if (wirelessConnectLineItem) {
      yield put(
        actions.disableConfig(internetLineItemTypes.wirelessConnect,
          item.isSelected)
      )
    }

    if (wirelessConnectExtBatLineItem) {
      yield put(
        actions.disableConfig(internetLineItemTypes.wirelessConnectExtBat,
          item.isSelected)
      )
    }
  }
}

function* rootSaga() {
  yield takeLatest(
    actions.CONFIG_ITEM_INTERNET,
    configurationLineItemSaga(lineItemSagas)
  )

  yield takeLatest(actions.TOGGLE_INTERNET_OPTIONS, toggleInternetOptions)
}

export default rootSaga
